import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, KeyRound, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { PasswordField } from '@/components/ui/password-field';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { Field, ResetPasswordModal, Select } from './IamPage';

interface Member {
  user_id: string;
  username: string;
  display_name: string;
  email?: string | null;
  role: 'TENANT_ADMIN' | 'TENANT_MEMBER' | 'SUPER_ADMIN';
  tenant_id: string | null;
  status: 'active' | 'disabled';
  created_at: string;
  last_login_at?: string | null;
}

async function extractDetail(err: unknown): Promise<string> {
  try {
    if (err && typeof err === 'object' && 'response' in err) {
      const res = (err as { response: Response }).response;
      const body = await res.clone().json();
      return body?.detail ?? `HTTP ${res.status}`;
    }
  } catch {
    /* fall through */
  }
  return err instanceof Error ? err.message : '网络异常';
}

export function MembersPage() {
  const role = useAuthStore((s) => s.user?.role);
  const tenantId = useAuthStore((s) => s.user?.tenant_id);
  const selfId = useAuthStore((s) => s.user?.user_id);
  const qc = useQueryClient();
  const isAdmin = role === 'TENANT_ADMIN' || role === 'SUPER_ADMIN';
  const { data: members = [], isLoading } = useQuery<Member[]>({
    queryKey: ['iam', 'members'],
    queryFn: () => api.get('iam/members').json<Member[]>(),
    enabled: isAdmin,
  });
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<Member | null>(null);
  const [deleting, setDeleting] = useState<Member | null>(null);

  if (!isAdmin) {
    return (
      <div className="space-y-6 p-6 lg:p-8">
        <div className="rounded-xl border border-danger/30 bg-danger/5 p-6 text-sm text-danger">
          需要租户管理员权限。
        </div>
      </div>
    );
  }
  if (role === 'SUPER_ADMIN') {
    return (
      <div className="space-y-6 p-6 lg:p-8">
        <div className="rounded-xl border border-warning/30 bg-warning/5 p-6 text-sm text-neutral-700">
          你是超级管理员,本页是租户管理员视角。请改用「身份与租户」(`/admin/iam`)管理所有租户用户。
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 lg:p-8">
      <header>
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <Users className="h-3.5 w-3.5 text-brand-500" /> 租户 {tenantId}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">成员</h1>
        <p className="mt-1 text-sm text-neutral-600">管理本租户下的成员账号</p>
      </header>

      <div className="flex items-center justify-between">
        <p className="text-xs text-neutral-500">共 {members.length} 个成员</p>
        <Button size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> 新建成员
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b border-outline-variant/30 bg-surface-variant/40 text-xs text-neutral-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">账号</th>
                <th className="px-4 py-2 text-left font-medium">显示名</th>
                <th className="px-4 py-2 text-left font-medium">角色</th>
                <th className="px-4 py-2 text-left font-medium">状态</th>
                <th className="px-4 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-xs text-neutral-400">
                    加载中…
                  </td>
                </tr>
              )}
              {!isLoading && members.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-xs text-neutral-400">
                    暂无成员
                  </td>
                </tr>
              )}
              {members.map((m) => (
                <MemberRow
                  key={m.user_id}
                  m={m}
                  selfId={selfId}
                  onReset={() => setResetting(m)}
                  onDelete={() => setDeleting(m)}
                  onChanged={() => qc.invalidateQueries({ queryKey: ['iam', 'members'] })}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <NewMemberModal
        open={creating}
        onOpenChange={setCreating}
        onCreated={() => qc.invalidateQueries({ queryKey: ['iam', 'members'] })}
      />
      <ResetPasswordModal
        target={resetting}
        onOpenChange={(b) => !b && setResetting(null)}
        endpoint={(id) => `iam/members/${id}/reset-password`}
      />
      <DeleteMemberDialog
        target={deleting}
        onOpenChange={(b) => !b && setDeleting(null)}
        onDeleted={() => qc.invalidateQueries({ queryKey: ['iam', 'members'] })}
      />
    </div>
  );
}

function MemberRow({
  m,
  selfId,
  onReset,
  onDelete,
  onChanged,
}: {
  m: Member;
  selfId: string | undefined;
  onReset: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const isSelf = m.user_id === selfId;
  const toggle = useMutation({
    mutationFn: () =>
      api
        .patch(`iam/members/${m.user_id}`, {
          json: { status: m.status === 'active' ? 'disabled' : 'active' },
        })
        .json<Member>(),
    onSuccess: () => {
      toast.success('成员状态已更新');
      onChanged();
    },
    onError: async (e) => toast.error('更新失败', await extractDetail(e)),
  });
  return (
    <tr className="border-b border-outline-variant/15 last:border-0">
      <td className="px-4 py-2.5 font-mono text-xs text-neutral-700">{m.username}</td>
      <td className="px-4 py-2.5">{m.display_name}</td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex rounded px-1.5 py-0.5 text-[11px] ${
            m.role === 'TENANT_ADMIN'
              ? 'bg-brand-100 text-brand-700'
              : 'bg-neutral-100 text-neutral-700'
          }`}
        >
          {m.role === 'TENANT_ADMIN' ? '租管' : '成员'}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-[11px] ${
            m.status === 'active'
              ? 'bg-success/10 text-success'
              : 'bg-neutral-100 text-neutral-500'
          }`}
        >
          {m.status === 'active' ? '启用' : '已停用'}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="sm" disabled={isSelf} onClick={onReset}>
            <KeyRound className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isSelf}
            title={isSelf ? '不能删除自己' : undefined}
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5 text-danger" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isSelf || toggle.isPending}
            title={isSelf ? '不能停用/启用自己' : undefined}
            onClick={() => toggle.mutate()}
          >
            {m.status === 'active' ? '停用' : '启用'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function NewMemberModal({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  onCreated: () => void;
}) {
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'TENANT_ADMIN' | 'TENANT_MEMBER'>('TENANT_MEMBER');
  const create = useMutation({
    mutationFn: () =>
      api
        .post('iam/members', {
          json: {
            username: username.trim(),
            display_name: displayName.trim(),
            password,
            email: email.trim() || undefined,
            role: memberRole,
          },
        })
        .json<Member>(),
    onSuccess: () => {
      toast.success('成员已创建');
      onOpenChange(false);
      setUsername('');
      setDisplayName('');
      setPassword('');
      setEmail('');
      setMemberRole('TENANT_MEMBER');
      onCreated();
    },
    onError: async (e) => toast.error('创建失败', await extractDetail(e)),
  });
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="新建成员"
      description="可选择创建为租户成员或租户管理员"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
            创建
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
        className="space-y-3"
      >
        <Field label="账号">
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            pattern="^[a-zA-Z0-9_.-]{3,32}$"
            placeholder="3-32 位字母/数字/_.-"
            title="3-32 位,仅支持字母、数字、下划线、点、连字符"
            required
          />
        </Field>
        <Field label="显示名">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
        </Field>
        <Field label="密码">
          <PasswordField value={password} onChange={setPassword} />
        </Field>
        <Field label="邮箱(可选)">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="角色">
          <Select
            value={memberRole}
            onChange={(v) => setMemberRole(v as 'TENANT_ADMIN' | 'TENANT_MEMBER')}
            options={[
              { value: 'TENANT_MEMBER', label: '租户成员' },
              { value: 'TENANT_ADMIN', label: '租户管理员' },
            ]}
          />
        </Field>
      </form>
    </Modal>
  );
}

function DeleteMemberDialog({
  target,
  onOpenChange,
  onDeleted,
}: {
  target: Member | null;
  onOpenChange: (b: boolean) => void;
  onDeleted: () => void;
}) {
  const del = useMutation({
    mutationFn: () =>
      target ? api.delete(`iam/members/${target.user_id}`).then(() => undefined) : Promise.resolve(),
    onSuccess: () => {
      toast.success('成员已删除');
      onOpenChange(false);
      onDeleted();
    },
    onError: async (e) => toast.error('删除失败', await extractDetail(e)),
  });

  return (
    <ConfirmDialog
      open={!!target}
      onOpenChange={onOpenChange}
      title={target ? `删除成员 ${target.display_name}?` : '删除成员'}
      description={
        target ? `账号 ${target.username} 将被永久删除,此操作不可撤销。` : undefined
      }
      confirmText="确认删除"
      tone="danger"
      pending={del.isPending}
      onConfirm={() => del.mutate()}
    />
  );
}
